import React from 'react';
import { Box, Card, Typography, Chip } from '@mui/material';
import { Settings, Psychology, AutoAwesome, CheckCircle, Search, TrackChanges } from '@mui/icons-material';
import { QuestionCard } from './QuestionCard';
import { getLabel } from './interviewKitUtils';

export const SectionBlock = ({ sectionKey, sectionValue, sectionIdx }) => {
    const items = Array.isArray(sectionValue) ? sectionValue : [sectionValue];
    return (
        <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to right, #6f42c1, transparent)', borderRadius: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1d4ed8', px: 1, whiteSpace: 'nowrap', fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                    {getLabel(sectionKey)}
                </Typography>
                <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to left, #6f42c1, transparent)', borderRadius: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {items.map((item, idx) => {
                    if (typeof item === 'string') {
                        return (
                            <Card key={`str-${idx}`} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: 'none', display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Typography sx={{ color: '#3b82f6', fontWeight: 800, fontSize: '0.75rem' }}>{idx + 1}</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500, lineHeight: 1.6, pt: 0.3 }}>{item}</Typography>
                            </Card>
                        );
                    }
                    if (typeof item === 'object' && item !== null) {
                        return <QuestionCard key={`obj-${idx}`} item={item} idx={idx} />;
                    }
                    return null;
                })}
            </Box>
        </Box>
    );
};

export const PointsACreuser = ({ points }) => (
    <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to right, #f59e0b, transparent)', borderRadius: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#b45309', px: 1, whiteSpace: 'nowrap', fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: 1, display: 'flex', alignItems: 'center' }}>
                <Search fontSize="small" sx={{ mr: 0.5 }} /> Points à approfondir
            </Typography>
            <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to left, #f59e0b, transparent)', borderRadius: 1 }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {points.map((point, idx) => (
                <Card key={`pt-${idx}`} sx={{ p: 2, borderRadius: 2, border: '1px solid #fde68a', boxShadow: 'none', backgroundColor: '#fffbeb', display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#fbbf24', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.75rem' }}>{idx + 1}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#78350f', fontWeight: 500, lineHeight: 1.6, pt: 0.3 }}>{point}</Typography>
                </Card>
            ))}
        </Box>
    </Box>
);

const POIDS_COLORS = {
    eleve: { bg: '#fee2e2', color: '#991b1b' },
    moyen: { bg: '#fef3c7', color: '#92400e' },
    faible: { bg: '#f0fdf4', color: '#166534' },
};

export const CriteresBlock = ({ criteres }) => (
    <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to right, #22c55e, transparent)', borderRadius: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#15803d', px: 1, whiteSpace: 'nowrap', fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: 1, display: 'flex', alignItems: 'center' }}>
                <CheckCircle fontSize="small" sx={{ mr: 0.5 }} /> Critères d'évaluation
            </Typography>
            <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to left, #22c55e, transparent)', borderRadius: 1 }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {criteres.map((c, idx) => {
                const poidsKey = (c.poids || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                const poidsStyle = POIDS_COLORS[poidsKey] || { bg: '#f1f5f9', color: '#475569' };
                const critereLabel = c.critere || c.criteria || c.name || c.titre || Object.values(c)[0] || `Critère ${idx + 1}`;
                return (
                    <Card key={`crit-${idx}`} sx={{ p: 2, borderRadius: 2, border: '1px solid #bbf7d0', boxShadow: 'none', backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.75rem' }}>{idx + 1}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#14532d', fontWeight: 600, lineHeight: 1.5, flex: 1 }}>{critereLabel}</Typography>
                        {c.poids && (
                            <Chip label={`Poids : ${c.poids}`} size="small" sx={{ backgroundColor: poidsStyle.bg, color: poidsStyle.color, fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
                        )}
                    </Card>
                );
            })}
        </Box>
    </Box>
);

const TYPE_ORDER = ['Technique', 'Comportementale', 'Situationnelle', 'Autres'];

const groupByType = (questions) => {
    const grouped = {};
    questions.forEach((item, idx) => {
        const type = typeof item === 'object' && item.type ? item.type.toLowerCase().trim() : 'autres';
        let normalizedType = 'Autres';
        if (['technique', 'technical'].includes(type)) normalizedType = 'Technique';
        else if (['comportementale', 'behavioral'].includes(type)) normalizedType = 'Comportementale';
        else if (['situationnelle', 'situational'].includes(type)) normalizedType = 'Situationnelle';
        else if (typeof item === 'object' && item.type) normalizedType = item.type.charAt(0).toUpperCase() + item.type.slice(1);
        if (!grouped[normalizedType]) grouped[normalizedType] = [];
        grouped[normalizedType].push({ item, originalIdx: idx });
    });
    return grouped;
};

export const QuestionsSection = ({ questions }) => {
    const grouped = groupByType(questions);
    const sortedTypes = Object.keys(grouped).sort((a, b) => {
        const ia = TYPE_ORDER.indexOf(a) === -1 ? 99 : TYPE_ORDER.indexOf(a);
        const ib = TYPE_ORDER.indexOf(b) === -1 ? 99 : TYPE_ORDER.indexOf(b);
        return ia - ib;
    });

    return (
        <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to right, #6f42c1, transparent)', borderRadius: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1d4ed8', px: 1, whiteSpace: 'nowrap', fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: 1, display: 'flex', alignItems: 'center' }}>
                    <TrackChanges fontSize="small" sx={{ mr: 0.5 }} /> Questions d'entretien
                </Typography>
                <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to left, #6f42c1, transparent)', borderRadius: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {sortedTypes.map((typeKey, typeIdx) => (
                    <Box key={`type-${typeIdx}`} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#475569', borderBottom: '2px solid #e2e8f0', pb: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            {typeKey === 'Technique' && <Settings fontSize="small" />}
                            {typeKey === 'Comportementale' && <Psychology fontSize="small" />}
                            {typeKey === 'Situationnelle' && <AutoAwesome fontSize="small" />}
                            <span style={{ marginLeft: '4px' }}>{typeKey}</span>
                            <Chip label={grouped[typeKey].length} size="small" sx={{ ml: 1, backgroundColor: '#f1f5f9', fontWeight: 'bold' }} />
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {grouped[typeKey].map(({ item, originalIdx }) =>
                                typeof item === 'string'
                                    ? <Card key={`strq-${originalIdx}`} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: 'none', display: 'flex', gap: 2 }}>
                                        <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <Typography sx={{ color: '#3b82f6', fontWeight: 800, fontSize: '0.75rem' }}>{originalIdx + 1}</Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#334155', fontWeight: 500, lineHeight: 1.6, pt: 0.3 }}>{item}</Typography>
                                    </Card>
                                    : <QuestionCard key={`objq-${originalIdx}`} item={item} idx={originalIdx} />
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};
